// Building blocks for the page builder's inspector (SectionTab, PageTab,
// SectionFields and inspector/FunnelFields), drawn the way Flowmark's editor
// draws them: groups with a full-width hairline on top and 16px gutters,
// 12px labels above 32px fields, full-width segmented choices, 30x18 switches
// for on/off rows, and repeated items (a review, a plan, a question) as
// one-line cards that open to their fields.
//
// Every control has a visible 12px sentence-case label, so a filled field
// never loses its name the way a placeholder-only input does; placeholders are
// examples or "Optional". Options use Orbit's .seg control for 2 to 4 choices
// and a select for 5 or more. Nothing is a solid-black toggle button.
//
// Colors are app tokens (bg-surface, border-border, text-textDim...), so dark
// mode keeps working; hover and selected fills are ink at 4% and 8%.
//
// View only (InspectorReadOnly): the editor wraps the panel in a disabled
// fieldset, so every control is off; groups then stay open and item cards show
// their fields, so everything can still be read.
import { createContext, Fragment, useCallback, useContext, useEffect, useId, useLayoutEffect, useRef, useState, useSyncExternalStore, type ReactNode, type Ref } from 'react';
import { moveItem, type FooterLink } from '../model/pages';
import { ChevronDownIcon, ChevronRightIcon, ChevronUpIcon, ImageIcon, PlusIcon, TrashIcon, XIcon } from '../icons';
import { SegGlide } from './SegGlide';
import { DynamicTextPicker, useDynamicTextInsertion } from './DynamicTextField';

/** True inside a view-only inspector (people who can only view). */
export const InspectorReadOnly = createContext(false);
export const useInspectorReadOnly = () => useContext(InspectorReadOnly);

// ── Shared looks ───────────────────────────────────────────────────────────

/** Field label: 12px, medium, muted, sentence case (Orbit's .label). */
export const LABEL = 'label !mb-0';
/** One plain sentence under a field. */
export const HINT = 'text-xs leading-[1.45] text-textDim';

const FOCUS = 'focus:border-primary focus:ring-[3px] focus:ring-primary/[.18]';
const INVALID = 'aria-[invalid=true]:border-danger aria-[invalid=true]:focus:border-danger aria-[invalid=true]:focus:ring-danger/[.18]';
// Touch screens: 16px text so iOS never zooms into a field, and taller rows.
const COARSE_TEXT = '[@media(pointer:coarse)]:text-[16px]';

/** A 32px one-line field: 6px radius, hairline border, blue border and a soft
 *  3px blue ring on focus. */
export const INPUT = `input h-8 rounded-lg py-0 ${FOCUS} ${INVALID} ${COARSE_TEXT} [@media(pointer:coarse)]:h-10`;
/** A text box that starts at 64px and grows with its text. */
export const TEXTAREA = `input min-h-16 resize-y rounded-lg py-[7px] leading-normal ${FOCUS} ${INVALID} ${COARSE_TEXT}`;
/** One line of text that wraps instead of scrolling sideways (a headline):
 *  32px tall like an input, taller as it wraps. */
const TEXTAREA_LINE = `input min-h-8 resize-none rounded-lg py-[6px] leading-[18px] ${FOCUS} ${INVALID} ${COARSE_TEXT}`;
/** A 32px select with Orbit's chevron. */
export const SELECT = `select h-8 rounded-lg py-0 ${FOCUS} ${COARSE_TEXT} [@media(pointer:coarse)]:h-10`;
/** Code, hex colors and other typed-in values. */
export const MONO = 'font-mono text-xs';

/** Gives inputs that come from shared components (the image field's link
 *  box, a select inside a panel) the same 32px, 6px radius and blue focus as
 *  the fields above (a field marked invalid keeps its red). Put it on the
 *  inspector panel's root. */
export const INSPECTOR_SCOPE = [
  '[&_.input]:rounded-lg [&_input.input]:h-8 [&_input.input]:py-0 [&_select.select]:h-8 [&_select.select]:py-0',
  '[&_.input:not([aria-invalid=true]):focus]:border-primary [&_.input:not([aria-invalid=true]):focus]:ring-[3px] [&_.input:not([aria-invalid=true]):focus]:ring-primary/[.18]',
  '[@media(pointer:coarse)]:[&_input.input]:h-10 [@media(pointer:coarse)]:[&_select.select]:h-10 [@media(pointer:coarse)]:[&_.input]:text-[16px]',
].join(' ');

const ICON_BASE = 'inline-grid h-7 w-7 shrink-0 place-items-center rounded-lg transition duration-150 active:scale-[.94] disabled:pointer-events-none disabled:opacity-40 [@media(pointer:coarse)]:h-9 [@media(pointer:coarse)]:w-9';
/** A 28px icon button: gray icon, a faint ink fill on hover. */
export const ICON_BTN = `${ICON_BASE} text-textDim hover:bg-text/[0.04] hover:text-text`;
/** The same, red on hover (delete, remove). */
export const ICON_BTN_DANGER = `${ICON_BASE} text-textDim hover:bg-danger/10 hover:text-danger`;
/** A quiet 24px text button for a group head ("+ New form", "Same as brand"). */
export const TEXT_BTN = 'inline-flex h-6 shrink-0 items-center gap-1 rounded px-1.5 text-xs font-medium text-text/80 transition-colors duration-100 hover:bg-text/[0.04] hover:text-text disabled:pointer-events-none disabled:opacity-40 [@media(pointer:coarse)]:h-9';
/** A 32px secondary button ("Edit form"). */
export const BTN = 'btn-ghost h-8 shrink-0 rounded-lg py-0 [@media(pointer:coarse)]:h-10';
/** A full-width "+ Add item" row under a list. */
const ADD_ROW = 'flex h-8 w-full min-w-0 items-center gap-2 rounded-lg px-2 text-left text-sm text-textDim transition-colors duration-100 hover:bg-text/[0.04] hover:text-text focus-visible:ring-inset focus-visible:ring-offset-0 [@media(pointer:coarse)]:h-11';

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** The last path segment of an image URL ("avatar-01.webp"), for a row summary.
 *  Works for links on the page's own host too (/template-assets/...). Empty for
 *  data: and blob: URLs and blanks. */
export function fileLabel(url: string | undefined): string {
  const u = (url ?? '').trim();
  if (!u || u.startsWith('data:') || u.startsWith('blob:')) return '';
  try {
    const last = new URL(u, 'https://host.invalid').pathname.split('/').filter(Boolean).pop() ?? '';
    return decodeURIComponent(last);
  } catch {
    return u;
  }
}

// ── Groups ─────────────────────────────────────────────────────────────────

const BLOCK_KEY = 'nla:page-inspector:block:';
const blockListeners = new Set<() => void>();
// Only used when localStorage throws (private mode, blocked site data), so a
// toggle still sticks for the rest of the visit.
const blockMemory = new Map<string, boolean>();

function readBlockOpen(name: string, fallback: boolean): boolean {
  try {
    const raw = window.localStorage.getItem(BLOCK_KEY + name);
    if (raw === '1' || raw === '0') return raw === '1';
  } catch { /* storage unavailable: memory below */ }
  return blockMemory.get(name) ?? fallback;
}

function writeBlockOpen(name: string, open: boolean) {
  try { window.localStorage.setItem(BLOCK_KEY + name, open ? '1' : '0'); }
  catch { blockMemory.set(name, open); }
  blockListeners.forEach((fn) => fn());
}

function subscribeBlocks(fn: () => void) {
  blockListeners.add(fn);
  return () => { blockListeners.delete(fn); };
}

/** Open state of a named inspector group. Shared by every group with that name
 *  (Media, Button, Social proof, Style, Colors...) and remembered in
 *  localStorage, so closing Style once keeps it closed on every section and
 *  every visit. */
export function useBlockOpen(name: string, defaultOpen: boolean): [boolean, (open: boolean) => void] {
  const open = useSyncExternalStore(subscribeBlocks, () => readBlockOpen(name, defaultOpen), () => defaultOpen);
  const set = useCallback((next: boolean) => writeBlockOpen(name, next), [name]);
  return [open, set];
}

/** A titled group of inspector fields with a full-width hairline on top.
 *  Static groups (collapsible={false}) always show their fields under a 40px
 *  head, with room for one action on the right (a switch, "+ New form").
 *  Collapsible groups have a 44px head with a chevron that turns when open;
 *  their open state is remembered per name. */
export function Block({ name, title, defaultOpen = false, collapsible = true, action, children }: {
  /** Storage key and default title: "Content", "Media", "Button", "Social proof", "Style". */
  name: string;
  title?: string;
  /** Collapsible groups only: open until the person closes it. */
  defaultOpen?: boolean;
  /** false: always open, no chevron (Flowmark's content groups). */
  collapsible?: boolean;
  /** Static groups only: a control on the right of the head. */
  action?: ReactNode;
  children: ReactNode;
}) {
  const [open, setOpen] = useBlockOpen(name, defaultOpen);
  const panelId = useId();
  const heading = title ?? name;
  const readOnly = useInspectorReadOnly();
  if (!collapsible || readOnly) {
    return (
      <section className="min-w-0 border-t border-border" data-block={name}>
        <div className="flex min-h-10 min-w-0 items-center justify-between gap-2 px-4 pt-2.5">
          <h3 className="min-w-0 text-sm font-semibold tracking-[-0.01em] text-text">{heading}</h3>
          {action}
        </div>
        <div className="grid min-w-0 gap-3.5 px-4 pb-4 pt-2.5">{children}</div>
      </section>
    );
  }
  return (
    <section className="min-w-0 border-t border-border" data-block={name}>
      <button type="button" aria-expanded={open} aria-controls={open ? panelId : undefined} onClick={() => setOpen(!open)}
        className="group flex min-h-11 w-full min-w-0 items-center gap-1.5 px-4 text-left text-sm font-semibold tracking-[-0.01em] text-text focus-visible:ring-inset focus-visible:ring-offset-0">
        <ChevronRightIcon size={14}
          className={`text-textDim transition-transform duration-150 ease-[cubic-bezier(.2,0,0,1)] group-hover:text-text motion-reduce:transition-none ${open ? 'rotate-90' : ''}`} />
        {heading}
      </button>
      {open && <div id={panelId} className="grid min-w-0 gap-3.5 px-4 pb-4">{children}</div>}
    </section>
  );
}

// ── Fields ─────────────────────────────────────────────────────────────────

type FieldExtras = { hint?: ReactNode; error?: ReactNode; className?: string };

function Notes({ id, hint, error }: { id: string; hint?: ReactNode; error?: ReactNode }) {
  return (
    <>
      {error ? <span id={`${id}-error`} className="text-xs text-danger">{error}</span> : null}
      {hint ? <span id={`${id}-hint`} className={HINT}>{hint}</span> : null}
    </>
  );
}

/** The ids a control's aria-describedby points at (its error, then its hint). */
export function describedBy(id: string, hint?: ReactNode, error?: ReactNode): string | undefined {
  const ids = [error ? `${id}-error` : '', hint ? `${id}-hint` : ''].filter(Boolean);
  return ids.length ? ids.join(' ') : undefined;
}

/** A labeled slot for a custom control: the label (with an optional value or
 *  action on its right), the control, then an error and a hint. `htmlFor`
 *  ties the label to the control; give the control
 *  aria-describedby={describedBy(htmlFor, hint, error)}. */
export function Field({ label, htmlFor, labelId, aside, hint, error, className, children }: FieldExtras & {
  label: ReactNode;
  htmlFor?: string;
  labelId?: string;
  /** Right side of the label row: a live value ("80% glass") or a text button. */
  aside?: ReactNode;
  children: ReactNode;
}) {
  const fallbackId = useId();
  const noteId = htmlFor ?? fallbackId;
  const labelEl = htmlFor
    ? <label htmlFor={htmlFor} id={labelId} className={`${LABEL} min-w-0`}>{label}</label>
    : <span id={labelId} className={`${LABEL} min-w-0`}>{label}</span>;
  return (
    <div className={`grid min-w-0 gap-1.5 ${className ?? ''}`}>
      {aside ? <div className="flex min-w-0 items-center justify-between gap-2">{labelEl}{aside}</div> : labelEl}
      {children}
      <Notes id={noteId} hint={hint} error={error} />
    </div>
  );
}

/** A one-line text input with its label above. */
export function TextField({ label, value, onChange, placeholder, type, inputClassName, hint, error, className, mono, maxLength, inputRef, ariaLabel, dynamic = false }: FieldExtras & {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: 'datetime-local';
  inputClassName?: string;
  /** Code-like values: monospace, 12px, no spellcheck. */
  mono?: boolean;
  maxLength?: number;
  inputRef?: Ref<HTMLInputElement>;
  /** A name for the input when the visible label is not enough. */
  ariaLabel?: string;
  /** Opt in only for visible copy handled by mergeSection, never URLs/config. */
  dynamic?: boolean;
}) {
  const id = useId();
  const ref = useRef<HTMLInputElement>(null);
  const readOnly = useInspectorReadOnly();
  const insert = useDynamicTextInsertion(ref, value, onChange, maxLength);
  return (
    <div className={`grid min-w-0 gap-1.5 ${className ?? ''}`}>
      <div className="flex min-w-0 items-center justify-between gap-2"><label htmlFor={id} className={LABEL}>{label}</label>
        {dynamic && !mono && !type && <DynamicTextPicker label={ariaLabel ?? label} disabled={readOnly} onInsert={insert} />}</div>
      <input id={id} ref={node => { ref.current = node; if (typeof inputRef === 'function') inputRef(node); else if (inputRef) inputRef.current = node; }} type={type} className={`${INPUT} ${mono ? MONO : ''} ${inputClassName ?? ''}`} value={value} placeholder={placeholder}
        maxLength={maxLength} spellCheck={mono ? false : undefined} aria-label={ariaLabel}
        aria-invalid={error ? true : undefined} aria-describedby={describedBy(id, hint, error)}
        onChange={(e) => onChange(e.target.value)} />
      <Notes id={id} hint={hint} error={error} />
    </div>
  );
}

/** A multi-line text box with its label above. It grows with its text, up to
 *  240px (420px for code). `singleLine`: one line of text that wraps to show
 *  all of itself (a headline); Enter adds no line break and pasted breaks
 *  become spaces. */
export function TextAreaField({ label, value, onChange, placeholder, rows, inputClassName, hint, error, className, mono, maxLength, singleLine, dynamic = false }: FieldExtras & {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  rows?: number;
  inputClassName?: string;
  mono?: boolean;
  maxLength?: number;
  singleLine?: boolean;
  /** Opt in only for visible copy handled by mergeSection, never code. */
  dynamic?: boolean;
}) {
  const id = useId();
  const ref = useRef<HTMLTextAreaElement>(null);
  const readOnly = useInspectorReadOnly();
  const insert = useDynamicTextInsertion(ref, value, onChange, maxLength);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    const h = el.scrollHeight;
    // No layout (a hidden panel, or a test DOM): keep the CSS height.
    if (!h) { el.style.height = ''; return; }
    el.style.height = `${Math.min(h + 2, mono ? 420 : 240)}px`;
  }, [value, mono]);
  return (
    <div className={`grid min-w-0 gap-1.5 ${className ?? ''}`}>
      <div className="flex min-w-0 items-center justify-between gap-2"><label htmlFor={id} className={LABEL}>{label}</label>
        {dynamic && !mono && <DynamicTextPicker label={label} disabled={readOnly} onInsert={insert} />}</div>
      <textarea id={id} ref={ref} rows={singleLine ? 1 : rows} className={`${singleLine ? TEXTAREA_LINE : TEXTAREA} ${mono ? MONO : ''} ${inputClassName ?? ''}`} value={value} placeholder={placeholder}
        maxLength={maxLength} spellCheck={mono ? false : undefined}
        aria-invalid={error ? true : undefined} aria-describedby={describedBy(id, hint, error)}
        onKeyDown={singleLine ? (e) => { if (e.key === 'Enter') e.preventDefault(); } : undefined}
        onChange={(e) => onChange(singleLine ? e.target.value.replace(/\s*[\r\n]+\s*/g, ' ') : e.target.value)} />
      <Notes id={id} hint={hint} error={error} />
    </div>
  );
}

/** A labeled select. Options come in as <option> children. */
export function SelectField({ label, value, onChange, children, hint, className }: FieldExtras & {
  label: string;
  value: string;
  onChange: (v: string) => void;
  children: ReactNode;
}) {
  const id = useId();
  return (
    <div className={`grid min-w-0 gap-1.5 ${className ?? ''}`}>
      <label htmlFor={id} className={LABEL}>{label}</label>
      <select id={id} className={SELECT} value={value} aria-describedby={describedBy(id, hint)}
        onChange={(e) => onChange(e.target.value)}>{children}</select>
      <Notes id={id} hint={hint} />
    </div>
  );
}

/** Flowmark's 30x18 switch: a gray track that turns ink when on, with a 14px
 *  knob. A real checkbox (role switch) sits on top, so a click, Space and a
 *  <label for> all work. */
export function Switch({ checked, onChange, id, label, labelledBy, describedBy: described, disabled }: {
  checked: boolean;
  onChange: (on: boolean) => void;
  id?: string;
  /** Accessible name when no <label for={id}> names it. */
  label?: string;
  labelledBy?: string;
  describedBy?: string;
  disabled?: boolean;
}) {
  return (
    <span className="relative inline-flex h-[18px] w-[30px] shrink-0">
      <input id={id} type="checkbox" role="switch" checked={checked} disabled={disabled}
        aria-label={label} aria-labelledby={labelledBy} aria-describedby={described}
        onChange={(e) => onChange(e.target.checked)}
        className="peer absolute inset-0 z-[1] m-0 h-full w-full cursor-pointer opacity-0 disabled:cursor-not-allowed" />
      <span aria-hidden className="pointer-events-none absolute inset-0 rounded-full bg-borderHi transition-colors duration-150 peer-checked:bg-text peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-primary peer-disabled:opacity-40" />
      <span aria-hidden className="pointer-events-none absolute left-0.5 top-0.5 h-3.5 w-3.5 rounded-full bg-white shadow-[0_1px_2px_rgb(0_0_0/0.2)] transition-transform duration-[180ms] ease-[cubic-bezier(.2,0,0,1)] peer-checked:translate-x-3 peer-checked:bg-surface motion-reduce:transition-none" />
    </span>
  );
}

/** An on/off row: the label (and an optional one-line hint) on the left, a
 *  switch on the right. Clicking the label flips it too. */
export function Check({ label, checked, onChange, hint, aside }: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  hint?: ReactNode;
  /** Something small between the label and the switch (a "Set up" link). */
  aside?: ReactNode;
}) {
  const id = useId();
  return (
    <div className="flex min-h-6 min-w-0 items-center gap-3 [@media(pointer:coarse)]:min-h-11">
      <span className="grid min-w-0 flex-1 gap-0.5">
        <label htmlFor={id} className="cursor-pointer text-sm text-text">{label}</label>
        {hint ? <span id={`${id}-hint`} className={HINT}>{hint}</span> : null}
      </span>
      {aside}
      <Switch id={id} checked={checked} onChange={onChange} describedBy={hint ? `${id}-hint` : undefined} />
    </div>
  );
}

/** The same on/off row, by its Flowmark name. */
export const Toggle = Check;

export type ChoiceOptions<T extends string> = readonly (readonly [T, string])[];

/** Orbit's segmented control, for 2 to 4 options, stretched across the panel
 *  in equal parts (a long label takes the room it needs, the rest share what
 *  is left). The active option is a white tile on the recessed track, never a
 *  solid-black button. */
export function Seg<T extends string>({ value, options, onChange, labelledBy, itemLabel }: {
  value: T;
  options: ChoiceOptions<T>;
  onChange: (v: T) => void;
  labelledBy: string;
  /** Accessible name per option when its visible text is terse ("S" → "Text size S"). */
  itemLabel?: (label: string) => string;
}) {
  return (
    <div role="group" aria-labelledby={labelledBy} className="seg pe-glide-host flex w-full min-w-0 rounded-lg">
      <SegGlide active={Math.max(0, options.findIndex(([v]) => v === value))} />
      {options.map(([v, l]) => {
        const on = v === value;
        return (
          <button key={v} type="button" data-glide-item aria-pressed={on} aria-label={itemLabel?.(l)} onClick={() => onChange(v)}
            className={`seg-item h-[26px] flex-1 px-2 py-0 text-center [@media(pointer:coarse)]:h-9 ${on ? 'seg-item-active' : ''}`}>{l}</button>
        );
      })}
    </div>
  );
}

/** One choice from a short list, label above: a full-width segmented control
 *  for 2 to 4 options, a select for 5 or more. */
export function Choice<T extends string>({ label, value, options, onChange, itemLabel, hint }: {
  label: string;
  value: T;
  options: ChoiceOptions<T>;
  onChange: (v: T) => void;
  itemLabel?: (label: string) => string;
  hint?: ReactNode;
}) {
  const id = useId();
  if (options.length > 4) {
    return (
      <div className="grid min-w-0 gap-1.5">
        <label htmlFor={id} className={LABEL}>{label}</label>
        <select id={id} className={SELECT} value={value} aria-describedby={describedBy(id, hint)}
          onChange={(e) => onChange(e.target.value as T)}>
          {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <Notes id={id} hint={hint} />
      </div>
    );
  }
  return (
    <div className="grid min-w-0 gap-1.5">
      <span id={id} className={LABEL}>{label}</span>
      <Seg value={value} options={options} onChange={onChange} labelledBy={id} itemLabel={itemLabel} />
      <Notes id={id} hint={hint} />
    </div>
  );
}

/** A slider with its live value on the right of the label ("45%"). */
export function RangeField({ label, value, min, max, step, onChange, display, ariaLabel }: {
  label: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  onChange: (v: number) => void;
  /** What the value reads as; defaults to the number. */
  display?: ReactNode;
  ariaLabel?: string;
}) {
  const id = useId();
  return (
    <div className="grid min-w-0 gap-1.5">
      <div className="flex min-w-0 items-center justify-between gap-2">
        <label htmlFor={id} className={LABEL}>{label}</label>
        <span className="shrink-0 text-xs tnum text-textDim">{display ?? value}</span>
      </div>
      <input id={id} type="range" min={min} max={max} step={step} value={value} aria-label={ariaLabel}
        className="h-[18px] w-full min-w-0 cursor-pointer accent-text"
        onChange={(e) => onChange(Number(e.target.value))} />
    </div>
  );
}

// ── Lists ──────────────────────────────────────────────────────────────────

/** "+ Add review": a quiet full-width row under a list. */
export function AddButton({ onClick, children }: { onClick: () => void; children: ReactNode }) {
  return (
    <button type="button" className={ADD_ROW} onClick={onClick}>
      <PlusIcon size={14} />{children}
    </button>
  );
}

function RemoveButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button type="button" className={ICON_BTN_DANGER} aria-label={label} title="Remove" onClick={onClick}>
      <XIcon size={14} />
    </button>
  );
}

/** A labeled list of one-line text items (benefits, check list points). */
function InlineCopyInput({ label, value, placeholder, onChange }: { label: string; value: string; placeholder?: string; onChange: (value: string) => void }) {
  const ref = useRef<HTMLInputElement>(null);
  const readOnly = useInspectorReadOnly();
  const insert = useDynamicTextInsertion(ref, value, onChange);
  return <span className="flex min-w-0 flex-1 items-center gap-0.5">
    <input ref={ref} className={INPUT} aria-label={label} placeholder={placeholder} value={value} onChange={event => onChange(event.target.value)} />
    <DynamicTextPicker label={label} disabled={readOnly} onInsert={insert} />
  </span>;
}

export function StringList({ label, noun, items, onChange, placeholder, dynamic = false }: {
  label: string;
  /** Singular, lower case: "item" → "Add item", "Item 2", "Remove item 2". */
  noun: string;
  items: string[];
  onChange: (items: string[]) => void;
  placeholder?: string;
  dynamic?: boolean;
}) {
  const id = useId();
  const readOnly = useInspectorReadOnly();
  return (
    <div role="group" aria-labelledby={id} className="grid min-w-0 gap-1.5">
      <span id={id} className={LABEL}>{label}</span>
      {items.map((b, i) => (
        <div key={i} className="flex min-w-0 items-center gap-1">
          {dynamic ? <InlineCopyInput label={`${cap(noun)} ${i + 1}`} placeholder={placeholder} value={b}
            onChange={value => { const a = [...items]; a[i] = value; onChange(a); }} />
            : <input className={INPUT} aria-label={`${cap(noun)} ${i + 1}`} placeholder={placeholder} value={b}
              onChange={(e) => { const a = [...items]; a[i] = e.target.value; onChange(a); }} />}
          {!readOnly && <RemoveButton label={`Remove ${noun} ${i + 1}`} onClick={() => onChange(items.filter((_, k) => k !== i))} />}
        </div>
      ))}
      {!readOnly && <AddButton onClick={() => onChange([...items, ''])}>Add {noun}</AddButton>}
    </div>
  );
}

/** A labeled list of links: a Label and a Link column, one row per link. */
export function LinkList({ label, links, onChange, newHref, labelPlaceholder, hrefPlaceholder, noun = 'link', dynamic = false }: {
  label: string;
  links: FooterLink[];
  onChange: (links: FooterLink[]) => void;
  /** The href a new row starts with ("#", "https://", or ""). */
  newHref: string;
  labelPlaceholder?: string;
  hrefPlaceholder?: string;
  noun?: string;
  /** Applies to visible link labels only. Link destinations stay static. */
  dynamic?: boolean;
}) {
  const id = useId();
  const readOnly = useInspectorReadOnly();
  const set = (i: number, p: Partial<FooterLink>) => { const a = [...links]; a[i] = { ...a[i], ...p }; onChange(a); };
  return (
    <div role="group" aria-labelledby={id} className="grid min-w-0 gap-1.5">
      <span id={id} className={LABEL}>{label}</span>
      {links.length > 0 && (
        <div className="grid min-w-0 grid-cols-[minmax(0,1fr)_minmax(0,1fr)_1.75rem] items-center gap-x-1 gap-y-1.5 [@media(pointer:coarse)]:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_2.25rem]">
          <span aria-hidden className="text-xs text-textDim">Label</span>
          <span aria-hidden className="text-xs text-textDim">Link</span>
          <span aria-hidden />
          {links.map((l, i) => (
            <Fragment key={i}>
              {dynamic ? <InlineCopyInput label={`${cap(noun)} ${i + 1} label`} placeholder={labelPlaceholder} value={l.label}
                onChange={value => set(i, { label: value })} />
                : <input className={INPUT} aria-label={`${cap(noun)} ${i + 1} label`} placeholder={labelPlaceholder} value={l.label}
                  onChange={(e) => set(i, { label: e.target.value })} />}
              <input className={INPUT} aria-label={`${cap(noun)} ${i + 1} link`} placeholder={hrefPlaceholder} value={l.href}
                onChange={(e) => set(i, { href: e.target.value })} />
              {readOnly ? <span aria-hidden /> : <RemoveButton label={`Remove ${noun} ${i + 1}`} onClick={() => onChange(links.filter((_, k) => k !== i))} />}
            </Fragment>
          ))}
        </div>
      )}
      {!readOnly && <AddButton onClick={() => onChange([...links, { label: '', href: newHref }])}>Add {noun}</AddButton>}
    </div>
  );
}

function Thumb({ src }: { src?: string }) {
  const [failed, setFailed] = useState('');
  return src && src !== failed
    ? <img src={src} alt="" onError={() => setFailed(src)} className="h-5 w-5 shrink-0 rounded border border-border object-cover" />
    : (
      <span className="grid h-5 w-5 shrink-0 place-items-center rounded border border-dashed border-border text-textDim">
        <ImageIcon size={12} />
      </span>
    );
}

/** Repeated items (reviews, modules, features, plans, questions, steps, stats,
 *  people, logos, images) as one-line cards: a chevron, a title and a short
 *  summary. Clicking a card opens its fields below its head, one card at a
 *  time. Move up, Move down and Remove sit on the right of every head and show
 *  on hover, on keyboard focus and while the card is open (always on touch
 *  screens). A quiet row under the cards adds an item. */
export function ItemList<T>({ label, noun, items, onChange, make, title, summary, thumb, max, children }: {
  label: string;
  /** Singular, lower case: "review" → "Add review", "Review 2", "Remove review 2". */
  noun: string;
  items: T[];
  onChange: (items: T[]) => void;
  /** A new, empty item. */
  make: () => T;
  /** The card's title; blank falls back to "Review 2". */
  title: (item: T, i: number) => string | undefined;
  summary?: (item: T, i: number) => string | undefined;
  /** Cards show a small image when given (logos, gallery, people). */
  thumb?: (item: T, i: number) => string | undefined;
  max?: number;
  /** The open item's fields. `set` replaces the item. */
  children: (item: T, i: number, set: (next: T) => void) => ReactNode;
}) {
  const [open, setOpen] = useState<number | null>(null);
  const id = useId();
  const readOnly = useInspectorReadOnly();
  const listRef = useRef<HTMLDivElement>(null);
  // Where keyboard focus goes once the list re-renders after an add, move or
  // remove (first match wins), so it follows the item instead of dropping to
  // the page when the old card's buttons unmount.
  const focusNext = useRef<string[] | null>(null);
  useEffect(() => {
    const wanted = focusNext.current;
    if (!wanted) return;
    focusNext.current = null;
    for (const sel of wanted) {
      const el = listRef.current?.querySelector<HTMLElement>(sel);
      if (el) { el.focus(); return; }
    }
  });

  const Noun = cap(noun);
  const canAdd = !readOnly && (max === undefined || items.length < max);
  const set = (i: number) => (next: T) => { const a = [...items]; a[i] = next; onChange(a); };

  function add() {
    onChange([...items, make()]);
    setOpen(items.length);
    focusNext.current = ['[data-item-panel] input:not([type="file"]), [data-item-panel] textarea, [data-item-panel] select'];
  }
  function move(i: number, dir: -1 | 1) {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    onChange(moveItem(items, i, j));
    // The open card (if any) stays open as the cards swap.
    setOpen((cur) => (cur === i ? j : cur === j ? i : cur));
    focusNext.current = [`[data-item="${j}"] [data-move="${dir < 0 ? 'up' : 'down'}"]:not(:disabled)`, `[data-row="${j}"]`];
  }
  function remove(i: number) {
    onChange(items.filter((_, k) => k !== i));
    setOpen((cur) => (cur === null || cur === i ? null : cur > i ? cur - 1 : cur));
    const next = Math.min(i, items.length - 2); // the card that slides into place, else the one above
    focusNext.current = next >= 0 ? [`[data-row="${next}"]`, '[data-add]'] : ['[data-add]'];
  }

  return (
    <div role="group" aria-labelledby={id} className="grid min-w-0 gap-1.5">
      <span id={id} className={LABEL}>{label}</span>
      <div ref={listRef} className="grid min-w-0 gap-1.5">
        {items.map((item, i) => {
          const isOpen = readOnly || open === i;
          const name = title(item, i)?.trim() || `${Noun} ${i + 1}`;
          const sub = summary?.(item, i)?.trim();
          const panelId = `${id}-item-${i}`;
          return (
            <div key={i} data-item={i} data-open={isOpen || undefined}
              className={`group/item min-w-0 rounded-lg border bg-surface transition-colors duration-100 ${isOpen ? 'border-text/[0.12]' : 'border-border'}`}>
              <div className="flex min-w-0 items-center gap-0.5 p-0.5">
                <button type="button" data-row={i} aria-expanded={isOpen} aria-controls={isOpen ? panelId : undefined}
                  onClick={() => setOpen(isOpen ? null : i)}
                  className="flex h-[30px] min-w-0 flex-1 items-center gap-1.5 rounded-md px-1.5 text-left transition-colors duration-100 hover:bg-text/[0.04] focus-visible:ring-inset focus-visible:ring-offset-0 [@media(pointer:coarse)]:h-10">
                  <ChevronRightIcon size={14}
                    className={`text-textDim transition-transform duration-150 ease-[cubic-bezier(.2,0,0,1)] motion-reduce:transition-none ${isOpen ? 'rotate-90' : ''}`} />
                  {thumb && <Thumb src={thumb(item, i)} />}
                  <span data-title className={`truncate text-sm text-text ${sub ? 'max-w-[60%] shrink-0' : 'min-w-0 flex-1'}`}>{name}</span>
                  {sub && <span className="min-w-0 flex-1 truncate text-xs text-textDim">{sub}</span>}
                </button>
                {!readOnly && <span className={`flex shrink-0 items-center transition-opacity duration-100 ${isOpen ? 'opacity-100' : 'opacity-0 group-hover/item:opacity-100 group-focus-within/item:opacity-100 [@media(hover:none)]:opacity-100'}`}>
                  <button type="button" data-move="up" className={ICON_BTN}
                    aria-label={`Move ${noun} ${i + 1} up`} title="Move up" disabled={i === 0} onClick={() => move(i, -1)}>
                    <ChevronUpIcon size={14} />
                  </button>
                  <button type="button" data-move="down" className={ICON_BTN}
                    aria-label={`Move ${noun} ${i + 1} down`} title="Move down" disabled={i === items.length - 1} onClick={() => move(i, 1)}>
                    <ChevronDownIcon size={14} />
                  </button>
                  <button type="button" className={ICON_BTN_DANGER} aria-label={`Remove ${noun} ${i + 1}`} title="Remove"
                    onClick={() => remove(i)}>
                    <TrashIcon size={14} />
                  </button>
                </span>}
              </div>
              {isOpen && (
                <div id={panelId} data-item-panel className="grid min-w-0 gap-3 border-t border-border p-3">
                  {children(item, i, set(i))}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {canAdd && (
        <button type="button" data-add onClick={add} className={ADD_ROW}>
          <PlusIcon size={14} />Add {noun}
        </button>
      )}
    </div>
  );
}
